import React, { MouseEvent } from 'react';
import { useTranslation } from 'next-i18next';
import useFormatLocaleDate from '@hooks/orders-sales/use-local-language';
import clsx from 'clsx';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Environment from '@config/index';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import { getCurrentProduct, onImageError, productImage } from '../helper';
import { OrderSaleTab, State } from '../type';
import styles from '../products.module.scss';
import LoadMoreView from '../common-views/load-more';

const ProductsView: React.FC<{
  state: State;
  updateState: (s: State) => void;
  order: Order | null;
  products: Product[];
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  isFetchingNextPage?: boolean;
}> = ({ state, updateState, order, products, hasNextPage, onLoadMore, isFetchingNextPage }) => {
  const { formatLocaleDate } = useFormatLocaleDate();
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');
  const { t } = useTranslation('orders-sales');
  const currentProduct = getCurrentProduct(state);
  const currentProductId = currentProduct != null ? currentProduct.productId : null;
  const forTab: OrderSaleTab = order != null ? 'Order' : 'Sale';

  const onSellerClick = (productId: string, sellerId: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    sendAnalyticEvent('timeline_seller_profile', {
      product_id: productId,
      property: sellerId,
    });
  };

  return (
    <>
      {products.map((product) => {
        const {
          productId,
          productName,
          brandName,
          imageUrl,
          orderConfirmationDate,
          productPrice,
          externalRef,
          sellerFirstName,
          sellerId,
          sellerEarning,
          sellerPrice,
        } = product;

        const showSeller = !!sellerFirstName && !!sellerId;

        return (
          <button
            data-cy={product.productId}
            key={product.productId}
            className={styles.ordersSales__mainLeft__products__product}
            onClick={() => {
              updateState({
                ...state,
                orderProduct: order ? [order, product] : state.orderProduct,
                saleProduct: !order ? product : state.saleProduct,
              });
              sendAnalyticEvent('timeline_product_click', {
                label: order == null ? 'sales' : 'orders',
                product_id: product.productId,
              });
            }}
          >
            {forTab === 'Sale' && orderConfirmationDate && (
              <div className={styles.ordersSales__mainLeft__productHead}>{formatLocaleDate(orderConfirmationDate)}</div>
            )}
            <div
              className={clsx(
                styles.ordersSales__mainLeft__productData,
                currentProductId === productId ? styles['ordersSales__mainLeft__productData--active'] : '',
              )}
            >
              <div
                className={clsx(
                  styles.ordersSales__mainLeft__productData__info,
                  externalRef ? '' : styles.ordersSales__mainLeft__productData__info__neg,
                )}
              >
                <div className={styles.ordersSales__mainLeft__productData__info__left}>
                  <img
                    className={styles.ordersSales__mainLeft__productData__info__left__image}
                    src={productImage(imageUrl)}
                    alt={productName}
                    onError={onImageError}
                  />
                </div>
                <div className={styles.ordersSales__mainLeft__productData__info__right}>
                  <div className={styles.ordersSales__mainLeft__productData__info__right__name}>{brandName}</div>
                  <div className={styles.ordersSales__mainLeft__productData__info__right__type}>{productName}</div>

                  {forTab === 'Sale' && (
                    <p>
                      <span className={styles.ordersSales__mainLeft__productData__info__right__status}>
                        <span className={styles.ordersSales__mainLeft__productData__info__right__sellerPrice}>
                          {sellerPrice}
                        </span>{' '}
                        ({t('MY_ACCOUNT.ORDERS_SALES.LIST.YOU_EARN')} {sellerEarning})
                      </span>
                    </p>
                  )}

                  <div className={styles.ordersSales__mainLeft__productData__info__right__status}>
                    {t('MY_ACCOUNT.ORDERS_SALES.LIST.REF')}. {productId}
                  </div>

                  {externalRef && (
                    <div className={styles.ordersSales__mainLeft__productData__info__right__ref}>{externalRef}</div>
                  )}

                  {forTab === 'Order' && (
                    <div className={styles.ordersSales__mainLeft__productData__info__right__price}>{productPrice}</div>
                  )}
                </div>
              </div>

              {showSeller && (
                <div
                  className={clsx(
                    styles.ordersSales__mainLeft__seller,
                    styles.ordersSales__mainLeft__productData__info__right__type,
                  )}
                >
                  {t('MY_ACCOUNT.ORDERS_SALES.LIST.SOLD_BY')}
                  <a
                    className={styles.ordersSales__mainLeft__seller_name}
                    href={`${Environment.baseHost}/profile/${sellerId}`}
                    rel={'noreferrer'}
                    target={'_blank'}
                    onClick={onSellerClick(productId, sellerId)}
                  >
                    {sellerFirstName}
                  </a>
                </div>
              )}
            </div>
          </button>
        );
      })}

      {hasNextPage && (
        <LoadMoreView
          onClick={onLoadMore}
          loading={isFetchingNextPage}
        />
      )}
    </>
  );
};

export default ProductsView;
